<template>
	<div :class="cn('relative flex size-full flex-col', props.class)">
		<Card :class="cardRootVariants({ variant })" @click="goToCollectionPage">
			<CardHeader class="p-0">
				<Badge
					:variant="'secondary-dark'"
					class="max-h-6 max-w-[100px] bg-secondary-dark-1 px-2 py-0.5 font-matter-sq text-sm font-normal uppercase leading-normal tracking-[0.07rem] text-primary transition-colors duration-300 group-hover/card:bg-secondary-dark-2"
				>
					COLEÇÃO
				</Badge>
			</CardHeader>
			<CardContent :class="cardContentVariants({ variant })">
				<div :class="creatorInfoWrapperVariants({ variant })">
					<BrokerAvatarInfo
						:border="false"
						:show-company-avatar="false"
						:broker-src="collectionCreatorImageSrc"
						:avatar-class="creatorAvatarVariants({ variant })"
					>
					</BrokerAvatarInfo>
					<CardTitle
						class="font-matter-sq text-xl font-normal leading-normal tracking-[-0.025rem] text-primary md:text-2xl md:tracking-[-0.03rem]"
					>
						{{ creatorName }}
					</CardTitle>
				</div>

				<p :class="mainContentTextVariants({ variant })">
					{{ mainContentText }}
				</p>
			</CardContent>
			<CardFooter :class="footerVariants({ variant })">
				<div v-if="variant === 'rich-content'" class="flex items-center gap-2">
					<Badge
						v-for="state in curatedList.availableStates"
						:key="state"
						variant="secondary-dark"
						class="rounded-none border-none bg-secondary-dark-1 px-1 py-0.5 font-sans text-xs font-normal leading-normal text-primary-light-3 transition-colors duration-300 group-hover/card:bg-secondary-dark-2 lg:text-sm lg:leading-normal"
					>
						{{ state }}
					</Badge>
				</div>

				<NuxtLink v-slot="{ href }" custom :to="detailsUrl">
					<Button
						as="a"
						size="md"
						:href="href"
						variant="transparent"
						class="size-fit p-0 text-lg"
						@click.stop="goToCollectionPage"
					>
						<IconMaterialSymbolsNorthEast class="size-6" />
					</Button>
				</NuxtLink>
			</CardFooter>
		</Card>
	</div>
</template>
<script setup lang="ts">
import type { CuratedListCardProps } from '.';

import { BrokerAvatarInfo } from '@/components/BrokerAvatarInfo';
import { cn } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle
} from '~/components/ui/card';

import {
	cardContentVariants,
	cardRootVariants,
	creatorAvatarVariants,
	creatorInfoWrapperVariants,
	footerVariants,
	mainContentTextVariants
} from '.';

const { trackCardCollectionClicked } = useEvents();

const router = useRouter();
const props = withDefaults(defineProps<CuratedListCardProps>(), {
	pageName: 'HomePage',
	variant: 'default',
	class: ''
});

const goToCollectionPage = () => {
	trackCardCollectionClicked({
		[MixpanelProps.CURRENT_PAGE]: props.pageName,
		[MixpanelProps.SECTION_NAME]: 'collection properties',
		[MixpanelProps.DESTINATION_URL]: props.detailsUrl,
		[MixpanelProps.COLLECTION_NAME]: props.curatedList.name
	});
	router.push(props.detailsUrl);
};

const getImageSrc = useImage();

const creatorName = computed<string>(() =>
	props.variant === 'rich-content'
		? props.curatedList.creator_details.author.name
		: props.curatedList.creator_details.name
);

const collectionCreatorImageSrc = computed<string>(() => {
	const imageUrl =
		props.variant === 'rich-content'
			? props.curatedList.creator_details.author.imageUrl
			: props.curatedList.creator_details.image_url;

	if (!imageUrl) return '';

	return getImageSrc(imageUrl, {
		format: 'webp',
		width: 72
	});
});

const mainContentText = computed<string>(
	() => props.curatedList.creator_details.headline
);
</script>
